import { IonCol, IonGrid, IonImg, IonLabel, IonPage, IonRow, IonText } from '@ionic/react';
import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import ActionButton from '../../components/button/ActionButton';
import LoadingContainer from '../../components/loading/LoadingContainer';

import Toolbar2 from '../../layouts/Toolbar2';
import { CampaignDonateAction } from '../../redux/actions/Campaign';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import BackAppButton from './BackAppButton';

import './Donation.css';

interface Props extends RouteComponentProps<{
  is_app?: string,
  donation_id?: string
}> { }

const Donation: React.FC<Props> = ({ match }) => {
  const dispatch = useAppDispatch()

  const { loading, donate } = useAppSelector(state => state.campaignDonateSuccessGet)

  const animDuration = 3000
  const animDelay = 750

  const bgWidth = 582;
  const bgHeight = 569;
  const initAnimalWidth = 380;
  const initAnimalHeight = 183;
  const initHeartWidth = 48;
  const initHeartHeight = 48;
  const cloudTop = 120;

  const [animalsSize, setAnimalsSize] = useState({ width: "", height: "", left: "", top: "" });

  const [contentHeight, setContentHeight] = useState("");

  const [heart1Delay, setHeart1Delay] = useState(animDelay * 1);
  const [heart2Delay, setHeart2Delay] = useState(animDelay * 2);
  const [heart3Delay, setHeart3Delay] = useState(animDelay * 3);
  const [heart4Delay, setHeart4Delay] = useState(animDelay * 4);

  const [heart1Visible, setHeart1Visible] = useState("visible");
  const [heart2Visible, setHeart2Visible] = useState("hidden");
  const [heart3Visible, setHeart3Visible] = useState("hidden");
  const [heart4Visible, setHeart4Visible] = useState("hidden");

  const [heartContainerTop, setHeartContainerTop] = useState(0);
  const [heartContainerHeight, setHeartContainerHeight] = useState(0);
  const [heartSize, setHeartSize] = useState({ width: 0, height: 0, left: 0 });
  const [heartContainerOverflowHeight, setHeartContainerOverflowHeight] = useState(150);

  const [animPause, setAnimPause] = useState(true)

  useEffect(() => {
    dispatch(CampaignDonateAction(match.params.donation_id));
  }, [])

  useEffect(() => {
    function initGraphics() {
      const winWidth = window.innerWidth
      const winHeight = window.innerHeight

      let bgWinRatio = winWidth / bgWidth
      let finalBgWidth = bgWidth * bgWinRatio
      let finalBgHeight = bgHeight * bgWinRatio

      let animalsRatio = winWidth / initAnimalWidth

      let animalsWidth = (initAnimalWidth * animalsRatio)
      let animalsHeight = (initAnimalHeight * animalsRatio)
      let animalsLeft = 0;
      let animalsTop = (finalBgHeight - animalsHeight)

      let bottomContentHeight = winHeight - finalBgHeight

      let heartWidth = (initHeartWidth * animalsRatio) / 1.6
      let heartHeight = (initHeartHeight * animalsRatio) / 1.6
      let heartLeft = (animalsWidth / 2) - (heartWidth / 2)
      let _heartContainerTop = cloudTop * bgWinRatio;
      let _heartContainerHeight = animalsTop - _heartContainerTop + (13 * animalsRatio)

      setHeartContainerTop(_heartContainerTop)
      setHeartContainerHeight(_heartContainerHeight)
      setHeartSize({ width: heartWidth, height: heartHeight, left: heartLeft })
      setHeartContainerOverflowHeight(_heartContainerHeight * 1.2)

      setAnimalsSize({
        width: animalsWidth + "px",
        height: animalsHeight + "px",
        left: animalsLeft + "px",
        top: animalsTop + "px"
      })

      setContentHeight(bottomContentHeight + "px")

      setTimeout(function () {
        setAnimPause(false)
      }, 1000)
    }

    initGraphics()
  }, [])

  const heart1Anim = useSpring({
    config: { duration: animDuration },
    from: { y: 0 },
    to: { y: heartContainerOverflowHeight },
    delay: heart1Delay,
    loop: true,
    pause: animPause,
    onChange: (pos) => {
      if (pos.value.y === heartContainerOverflowHeight)
        setHeart1Delay(0)
    }
  })

  const heart2Anim = useSpring({
    config: { duration: animDuration },
    from: { y: 0 },
    to: { y: heartContainerOverflowHeight },
    delay: heart2Delay,
    loop: true,
    pause: animPause,
    onStart: () => {
      setHeart2Visible("visible")
    },
    onChange: (pos) => {
      if (pos.value.y === heartContainerOverflowHeight)
        setHeart2Delay(0)
    }
  })

  const heart3Anim = useSpring({
    config: { duration: animDuration },
    from: { y: 0 },
    to: { y: heartContainerOverflowHeight },
    delay: heart3Delay,
    loop: true,
    pause: animPause,
    onStart: () => {
      setHeart3Visible("visible")
    },
    onChange: (pos) => {
      if (pos.value.y === heartContainerOverflowHeight)
        setHeart3Delay(0)
    }
  })

  const heart4Anim = useSpring({
    config: { duration: animDuration },
    from: { y: 0 },
    to: { y: heartContainerOverflowHeight },
    delay: heart4Delay,
    loop: true,
    pause: animPause,
    onStart: () => {
      setHeart4Visible("visible")
    },
    onChange: (pos) => {
      if (pos.value.y === heartContainerOverflowHeight)
        setHeart4Delay(0)
    }
  })

  return <LoadingContainer loading={loading}>
    <IonPage className="payment-donation">
      {match.params.is_app != "1" ?
        <Toolbar2 hasBackButton transparent /> : null}

      <IonGrid>
        <IonRow>
          <IonCol>
            <IonImg className="bg" src="/assets/images/payment/nature_bg.svg" />
            <IonImg className="animals" src="/assets/images/payment/donation.svg" style={{ left: animalsSize.left, top: animalsSize.top, width: animalsSize.width, height: animalsSize.height }} />

            <div className="heart-container" style={{ top: heartContainerTop + "px", height: heartContainerHeight + "px" }}>
              <animated.div style={{ position: 'absolute', left: heartSize.left, top: heart1Anim.y }}>
                <IonImg src="/assets/images/payment/heart1.svg" style={{ visibility: heart1Visible, width: heartSize.width, height: heartSize.height }} />
              </animated.div>

              <animated.div style={{ position: 'absolute', left: heartSize.left, top: heart2Anim.y }}>
                <IonImg src="/assets/images/payment/heart3.svg" style={{ visibility: heart2Visible, width: heartSize.width, height: heartSize.height }} />
              </animated.div>

              <animated.div style={{ position: 'absolute', left: heartSize.left, top: heart3Anim.y }}>
                <IonImg src="/assets/images/payment/heart4.svg" style={{ visibility: heart3Visible, width: heartSize.width, height: heartSize.height }} />
              </animated.div>

              <animated.div style={{ position: 'absolute', left: heartSize.left, top: heart4Anim.y }}>
                <IonImg src="/assets/images/payment/heart2.svg" style={{ visibility: heart4Visible, width: heartSize.width, height: heartSize.height }} />
              </animated.div>
            </div>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol className="content" style={{ height: contentHeight }}>
            <IonRow className="title">
              <IonCol>
                <IonText>مبلغ اهدایی شما دریافت شد.</IonText>
                <IonLabel>از حمایت شما سپاسگزاریم!</IonLabel>

                {donate!.doc_link ?
                  <ActionButton color="red" href={donate!.doc_link} target="_blank" noMargin compact>دریاف سند هدیه درخت</ActionButton> : null}
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="6" className="ion-text-right">
                <IonLabel>بابت</IonLabel>
              </IonCol>
              <IonCol size="6" className="ion-text-left">
                <IonLabel className="value">{donate!.campaign}</IonLabel>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="6" className="ion-text-right">
                <IonLabel>کد پیگیری</IonLabel>
              </IonCol>
              <IonCol size="6" className="ion-text-left">
                <IonLabel className="value">{donate!.transaction_id}</IonLabel>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="6" className="ion-text-right">
                <IonLabel>مبلغ پرداختی</IonLabel>
              </IonCol>
              <IonCol size="6" className="ion-text-left">
                <IonLabel className="value">{donate!.amount}</IonLabel>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="6" className="ion-text-right">
                <IonLabel>تاریخ پرداخت</IonLabel>
              </IonCol>
              <IonCol size="6" className="ion-text-left">
                <IonLabel className="value">{donate!.date}</IonLabel>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol className="note-text">
                لطفا با اشتراک گذاری این صفحه در شبکه های اجتماعی از دوستان خود دعوت کنید تا از این پروژه حمایت کنند.
                {/* <IonButton onClick={() => history.goBack()} color="danger">دریافت رسید</IonButton> */}
                {/* <IonButton onClick={() => history.goBack()} color="secondary">اشتراک گذاری</IonButton> */}
              </IonCol>
            </IonRow>
            {match.params.is_app == "1" ?
              <div className="ion-text-center">
                <BackAppButton doOnClick="open" />
              </div>
              : null}
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonPage>
  </LoadingContainer>
}

export default Donation